import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  fetchSlaRules,
  fetchSlaEvents,
  fetchSlaEventDetail,
  updateSlaRule,
  remindSlaEvent,
  ignoreSlaEvent,
  resolveSlaEvent,
} from '@/services/api/sla'
import type { UpdateSlaRulePayload } from '@/types/sla'

export function useSlaRules() {
  return useQuery({
    queryKey: ['sla', 'rules'],
    queryFn: fetchSlaRules,
    staleTime: 30_000,
  })
}

export function useUpdateSlaRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ ruleId, payload }: { ruleId: number; payload: UpdateSlaRulePayload }) =>
      updateSlaRule(ruleId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sla', 'rules'] })
      qc.invalidateQueries({ queryKey: ['sla', 'list'] })
    },
  })
}

export function useSlaEventList(params: {
  page?: number
  page_size?: number
  event_status?: string
}) {
  return useQuery({
    queryKey: ['sla', 'list', params],
    queryFn: () => fetchSlaEvents(params),
    staleTime: 15_000,
  })
}

export function useSlaEventDetail(eventId: string | null) {
  return useQuery({
    queryKey: ['sla', 'detail', eventId],
    queryFn: () => fetchSlaEventDetail(eventId!),
    enabled: !!eventId,
  })
}

export function useRemindSlaEvent() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ eventId, note }: { eventId: string; note?: string }) =>
      remindSlaEvent(eventId, note),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sla'] })
    },
  })
}

export function useIgnoreSlaEvent() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ eventId, reason }: { eventId: string; reason: string }) =>
      ignoreSlaEvent(eventId, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sla'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useResolveSlaEvent() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ eventId, note }: { eventId: string; note: string }) =>
      resolveSlaEvent(eventId, note),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sla'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}
